import { COMPONENT_CLASS_PREFIX, ROOT_ELEMENT_KEY, DATA_PRESET_ATTRIBUTE, CSS_PRESET_PREFIX } from './constants'
import type { ElementToUpdate, ElementWithPreset, PolyfillData, PresetsPolyfillData, PresetPolyfillData } from './types'
import { supportsContainerStyleQueries } from './browserFeatureDetection'

/** Build the CSS custom-property name for a given preset variable key. */
const getCSSPropertyName = (key: string): string => `${CSS_PRESET_PREFIX}-${key}`

const getCurrentPresetFromElement = (element: Element, presetVariableKey: string): string => {
	const computedStyle = window.getComputedStyle(element)
	const specificPreset = computedStyle.getPropertyValue(getCSSPropertyName(presetVariableKey)).trim()
	return specificPreset || ''
}

const buildSelector = (elementKey: string, selector: string, componentId: string): string => {
	return elementKey === ROOT_ELEMENT_KEY
		? `.${COMPONENT_CLASS_PREFIX}${componentId}`
		: `.${COMPONENT_CLASS_PREFIX}${componentId} ${selector}`
}

/** Find DOM elements that match a single element configuration. */
const findElementsForConfig = (
	elementConfig: ElementWithPreset,
	componentId: string
): Array<{ element: Element; elementKey: string }> => {
	const finalSelector = buildSelector(elementConfig.elementKey, elementConfig.selector, componentId)
	const results: Array<{ element: Element; elementKey: string }> = []
	const domElements = document.querySelectorAll(finalSelector)
	domElements.forEach((domElement) => {
		results.push({ element: domElement, elementKey: elementConfig.elementKey })
	})
	return results
}

/** Pair each found element with its current preset value, ready to be written. */
const extractPresetFromElements = (
	elements: Array<{ element: Element; elementKey: string }>,
	presetVariableKey: string
): Array<ElementToUpdate> => {
	return elements.map(({ element, elementKey }) => ({
		element,
		preset: getCurrentPresetFromElement(element, presetVariableKey),
		elementKey,
	}))
}

/** Process a single polyfill data entry (one builder type) into a flat list of updates. */
const processPolyfillDataEntry = (polyfillData: PolyfillData): Array<ElementToUpdate> => {
	if (!polyfillData?.elementsWithPresets || !polyfillData?.componentId) {
		return []
	}

	const componentId = polyfillData.componentId
	const elementsToUpdate: Array<ElementToUpdate> = []

	polyfillData.elementsWithPresets.forEach((elementConfig) => {
		const foundElements = findElementsForConfig(elementConfig, componentId)
		const elementsWithPresets = extractPresetFromElements(foundElements, elementConfig.presetVariableKey)
		elementsToUpdate.push(...elementsWithPresets)
	})

	return elementsToUpdate
}

/** Prepare all elements that need preset updates across every builder type. */
const prepareElementsToUpdate = (polyfillDataEntries: Array<[string, PolyfillData]>): Array<ElementToUpdate> => {
	const elementsToUpdate: Array<ElementToUpdate> = []
	polyfillDataEntries.forEach(([_builderType, polyfillData]) => {
		const processedElements = processPolyfillDataEntry(polyfillData)
		elementsToUpdate.push(...processedElements)
	})
	return elementsToUpdate
}

/**
 * Write `data-preset` attributes for every prepared element.
 * Empty/whitespace preset values clear the attribute so styles fall back
 * to their default.
 */
const updateElementsWithPresets = (elementsToUpdate: Array<ElementToUpdate>): void => {
	elementsToUpdate.forEach(({ element, preset }) => {
		const hasPreset = preset && preset.trim()
		if (hasPreset) {
			element.setAttribute(DATA_PRESET_ATTRIBUTE, preset)
		} else {
			element.removeAttribute(DATA_PRESET_ATTRIBUTE)
		}
	})
}

/**
 * Apply the presets polyfill across every builder type in `presetsPolyfillData`.
 * No-ops when the browser supports CSS container style queries natively.
 */
export const processPresetsPolyfill = (presetsPolyfillData: PresetsPolyfillData): void => {
	const polyfillDataEntries = Object.entries(presetsPolyfillData) as Array<[string, PolyfillData]>
	if (polyfillDataEntries.length === 0 || supportsContainerStyleQueries()) {
		return
	}

	try {
		const elementsToUpdate = prepareElementsToUpdate(polyfillDataEntries)
		updateElementsWithPresets(elementsToUpdate)
	} catch (error) {
		console.error('Presets Polyfill - Direct Update Error:', error)
	}
}

/**
 * Apply the presets polyfill for a specific builder component instance only.
 * Used after targeted DOM updates (e.g. component re-renders in the editor)
 * to avoid re-walking every builder component on the page.
 */
export const applyPolyfillForComponent = (
	builderComponentId: string,
	processedPresetsPolyfillData: Record<string, PresetPolyfillData>
): void => {
	if (supportsContainerStyleQueries()) {
		return
	}

	const matchingBuilderType = Object.keys(processedPresetsPolyfillData).find(
		(builderType) => processedPresetsPolyfillData[builderType]?.componentId === builderComponentId
	)

	if (matchingBuilderType && processedPresetsPolyfillData[matchingBuilderType]) {
		const polyfillDataForComponent = {
			[matchingBuilderType]: processedPresetsPolyfillData[matchingBuilderType],
		}
		processPresetsPolyfill(polyfillDataForComponent)
	}
}

/**
 * Minimal structural shape for the second arg of createPolyfillDataWithComponentId.
 * Thunderbolt's `BuilderComponentsData` from `@wix/thunderbolt-symbols` is assignable
 * to this (it has the same `.components` map plus extra fields). wix-next builds a
 * matching object inline from clientSpecMap.
 */
export type BuilderComponentsData = {
	components: { [builderType: string]: { componentId: string } }
}

/**
 * Joins Becky's pagePolyfillData (keyed by componentType / builderType) with a
 * componentType -> componentId lookup so the runtime polyfill can build
 * .w-{componentId} CSS selectors. Output keys remain componentType.
 */
export const createPolyfillDataWithComponentId = (
	pagePolyfillData: Record<string, PresetPolyfillData>,
	builderComponentsData: BuilderComponentsData
): Record<string, PresetPolyfillData> => {
	if (!pagePolyfillData || !builderComponentsData) {
		return {}
	}

	const processedPresetsPolyfillData: Record<string, PresetPolyfillData> = {}

	const builderTypes = Object.keys(builderComponentsData.components)
	builderTypes.forEach((builderType) => {
		const data = builderComponentsData.components[builderType]
		if (pagePolyfillData[builderType]) {
			const pageElements = pagePolyfillData[builderType].elementsWithPresets.map(
				({ elementKey, selector, presetVariableKey }) => ({
					elementKey,
					selector,
					presetVariableKey,
				})
			)

			processedPresetsPolyfillData[builderType] = {
				componentId: data.componentId,
				elementsWithPresets: pageElements,
			}
		}
	})

	return processedPresetsPolyfillData
}
